<template>
  <div class="flex flex-col bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-sm overflow-hidden shadow-mc-sm w-full h-full">
    <div class="h-[3px] bg-mc-red shrink-0"></div>
    <div class="px-4 pt-3 pb-2 border-b border-mc-steel dark:border-line-on-dark shrink-0">
      <h1 class="font-display font-extrabold text-[15px] leading-tight text-mc-ink dark:text-on-dark m-0">{{ template.title || 'Gauge' }}</h1>
      <p class="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-muted dark:text-on-dark-muted mt-1">{{ formattedLastUpdate }}</p>
    </div>
    <div class="w-full flex-1 min-h-0 pt-3">
      <div ref="chartContainer" class="w-full h-full"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed, defineProps } from 'vue';
import { utcToLocal } from '@/tools/TimeFunctions';
import Plotly from 'plotly.js-dist';
import { BRAND } from '@/components/widgets/plotlyBrand';

const isDark = () => document.documentElement.classList.contains('dark');

const props = defineProps({
  widget: {
    type: Object,
    required: true
  },
  dataID: {
    type: String,
    required: true
  },
  timeFormat: {
    type: String,
    default: '24hour'
  },
  streamData: {
    type: Object,
    default: () => ({})
  },
  sourceErrors: {
    type: Object,
    default: () => ({})
  }
});

// Convert widget config to the template shape PlotlyGuage expected
const template = computed(() => {
  const { title, ...config } = props.widget;
  return { ...config, title };
});

const chartContainer = ref(null);
const lastUpdate = ref('Last Update:');
let resizeObserver = null;
let resizeTimeout = null;
let darkModeObserver = null;

const sourceNotFound = computed(() =>
  props.sourceErrors?.[props.widget.source]?.type === 'not_found'
    ? props.widget.source : null
);

const sourceData = computed(() => {
  if (!template.value?.source) return null;
  return props.streamData[template.value.source]?.lastValue || null;
});

const formattedLastUpdate = computed(() => {
  if (sourceNotFound.value) return `Source "${sourceNotFound.value}" not found`;
  if (!template.value?.source) return 'No source';
  const data = props.streamData[template.value.source];
  if (data?.lastUpdate) {
    return `Last Update: ${utcToLocal(data.lastUpdate, props.timeFormat)}`;
  }
  return 'Waiting for data...';
});

const createGaugeData = () => {
  const containerWidth = chartContainer.value?.offsetWidth || 300;
  const containerHeight = chartContainer.value?.offsetHeight || 210;
  const isSmall = containerWidth < 300 || containerHeight < 180;
  const dark = isDark();
  const fontColor = dark ? BRAND.onDark : BRAND.ink;
  const tickColor = dark ? BRAND.onDarkMuted : BRAND.inkMuted;
  const borderColor = dark ? BRAND.lineOnDark : BRAND.steel;
  return [{
    type: 'indicator',
    mode: 'gauge+number',
    value: null,
    number: {
      prefix: '',
      suffix: ` ${template.value.unit || ''}`,
      font: {
        size: isSmall ? 24 : 40,
        family: BRAND.fontDisplay,
        color: fontColor,
      },
      valueformat: template.value.valuePrecision || '.2f',
    },
    gauge: {
      axis: {
        range: null,
        tickwidth: 1,
        tickcolor: tickColor,
        tickfont: {
          family: BRAND.fontBody,
          color: tickColor,
        },
      },
      bar: { color: template.value.color },
      bgcolor: template.value.bgColor || 'transparent',
      borderwidth: 2,
      bordercolor: borderColor,
      steps: []
    }
  }];
};

const createLayout = () => {
  if (!chartContainer.value) return null;

  const containerWidth = chartContainer.value.offsetWidth;
  if (!containerWidth) return null;

  const containerHeight = chartContainer.value.offsetHeight || 210;
  const fontColor = isDark() ? BRAND.onDark : BRAND.ink;
  const layout = {
    width: containerWidth,
    height: containerHeight,
    margin: { t: 20, r: 25, l: 25, b: 25 },
    paper_bgcolor: 'transparent',
    font: {
      color: fontColor,
      family: BRAND.fontBody,
    }
  };
  if (sourceNotFound.value) {
    layout.annotations = [{
      text: 'N/A',
      font: { family: BRAND.fontDisplay, size: 36, color: fontColor },
      showarrow: false,
      x: 0.5,
      y: 0.25,
      xref: 'paper',
      yref: 'paper',
      xanchor: 'center',
      yanchor: 'middle',
    }];
  }
  return layout;
};

const config = {
  displaylogo: false,
  responsive: true,
  staticPlot: true
};

const getNumericValueFromSource = (docData) => {
  if (!docData) return null;

  let value;
  if (template.value.field) {
    value = parseFloat(docData[template.value.field]);
  } else if (template.value.calculate) {
    const { formula, denominator } = template.value.calculate;
    const variables = {};
    denominator.forEach((field) => {
      variables[field] = parseFloat(docData[field]);
    });
    const calculateFn = new Function(...denominator, `return ${formula}`);
    value = calculateFn(...Object.values(variables));
  }

  return Number.isFinite(value) ? value : null;
};

const getLatestGaugeValue = () => {
  const sourceValue = getNumericValueFromSource(sourceData.value);
  if (sourceValue !== null) return sourceValue;

  const currentRenderedValue = chartContainer.value?._fullData?.[0]?.value;
  if (Number.isFinite(currentRenderedValue)) return currentRenderedValue;

  if (Number.isFinite(template.value.value)) return template.value.value;
  return null;
};

const updateChart = () => {
  if (!chartContainer.value) return;

  const layout = createLayout();
  if (!layout) {
    requestAnimationFrame(updateChart);
    return;
  }

  const data = createGaugeData();

  if (template.value.prefix) {
    data[0].number.prefix = `${template.value.prefix} `;
  }

  if (Array.isArray(template.value.range)) {
    data[0].gauge.axis.range = template.value.range;
  }

  if (template.value.steps) {
    data[0].gauge.steps = template.value.steps.map(step => ({
      range: Array.isArray(step.range) ? step.range : [null, null],
      color: step.color,
      thickness: `${step.thickness || 1}`
    }));
  }

  const interval = ((template.value.range[1] - template.value.range[0]) / 6);
  const tickvals = Array.from({ length: 7 }, (_, i) => {
    const value = template.value.range[0] + interval * i;
    return interval < 1 ? parseFloat(value.toFixed(2)) : Math.round(value);
  });
  data[0].gauge.axis.tickvals = tickvals;

  if (template.value.delta) {
    data[0].mode = 'gauge+number+delta';
    data[0].delta = template.value.delta;
  }

  if (template.value.threshold) {
    data[0].gauge.threshold = {
      line: { color: isDark() ? BRAND.onDark : BRAND.ink, width: 4 },
      thickness: 1,
      value: template.value.threshold
    };
  }

  const retainedValue = getLatestGaugeValue();

  try {
    Plotly.newPlot(chartContainer.value, data, layout, config);
    if (retainedValue !== null) {
      Plotly.restyle(chartContainer.value, { 'value': [retainedValue] });
    }
  } catch (error) {
    console.error('Error plotting gauge:', error);
    requestAnimationFrame(updateChart);
  }
};

const updateValue = (docData) => {
  if (!chartContainer.value || !docData) return;

  const value = getNumericValueFromSource(docData);

  if (value !== null) {
    try {
      Plotly.restyle(chartContainer.value, { 'value': [value] });
    } catch (error) {
      console.error('Error updating gauge value:', error);
    }
  }
};

watch(sourceNotFound, () => {
  updateChart();
});

watch(sourceData, (newData) => {
  if (newData) {
    updateValue(newData);
  }
}, { deep: true });

watch(formattedLastUpdate, (newUpdate) => {
  lastUpdate.value = newUpdate;
});

const debouncedUpdateChart = () => {
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }
  resizeTimeout = setTimeout(() => {
    if (chartContainer.value) {
      const currentValue = chartContainer.value._fullData?.[0]?.value ?? null;
      updateChart();
      if (currentValue !== null && !isNaN(currentValue)) {
        setTimeout(() => {
          if (chartContainer.value) {
            try {
              Plotly.restyle(chartContainer.value, { 'value': [currentValue] });
            } catch (error) {
              console.error('Error restoring value after resize:', error);
            }
          }
        }, 50);
      }
    }
  }, 250);
};

watch(() => template.value, (newTemplate) => {
  if (newTemplate.value !== undefined && newTemplate.value !== null) {
    updateChart();
    setTimeout(() => {
      if (chartContainer.value) {
        try {
          Plotly.restyle(chartContainer.value, { 'value': [newTemplate.value] });
        } catch (error) {
          console.error('Error updating preview value:', error);
        }
      }
    }, 100);
  } else {
    updateChart();
  }
}, { deep: true });

onMounted(() => {
  if (template.value.value !== undefined && template.value.value !== null) {
    updateChart();
    setTimeout(() => {
      if (chartContainer.value) {
        try {
          Plotly.restyle(chartContainer.value, { 'value': [template.value.value] });
        } catch (error) {
          console.error('Error setting preview value:', error);
        }
      }
    }, 100);
    lastUpdate.value = 'Preview Mode';
  } else {
    updateChart();

    if (sourceData.value) {
      updateValue(sourceData.value);
      lastUpdate.value = formattedLastUpdate.value;
    }
  }

  resizeObserver = new ResizeObserver(() => {
    debouncedUpdateChart();
  });

  if (chartContainer.value) {
    resizeObserver.observe(chartContainer.value);
  }

  darkModeObserver = new MutationObserver(() => {
    debouncedUpdateChart();
  });
  darkModeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
});

onUnmounted(() => {
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }
  if (resizeObserver && chartContainer.value) {
    resizeObserver.unobserve(chartContainer.value);
    resizeObserver.disconnect();
  }
  if (darkModeObserver) {
    darkModeObserver.disconnect();
  }
  if (chartContainer.value) {
    Plotly.purge(chartContainer.value);
  }
});
</script>
